import { filter, map, tap } from 'rxjs';
import { computed, isDevMode } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormState, ReportTabRulesKey } from '@navstation-portal/reporting/data-model/vessel-report';
import { signalStoreFeature, withComputed } from '@ngrx/signals';
import { withEventHandlers } from '@ngrx/signals/events';
import { CalculatedValueWrite, FormStoreEventGroup, formatCalculatedValuePath } from './create-form-store-events';
import { getStateFieldFromFormState } from './vm-builder-helpers';

/**
 * One Calculated Value of a tab: where the store holds it, and its value now.
 *
 * `rule` names the template field that holds its rules, when that is not the
 * state path. A row section is the usual case: the store holds the rows in one
 * array, and the template holds one field for each row. `rule.tabId` names the
 * tab of those rules, when another tab draws the field. Shore power is the
 * case: it shows on the Power tab or on the Operational tab.
 */
export interface CalculatedValue extends CalculatedValueWrite {
  rule?: { sectionId: string; fieldId: string; tabId?: ReportTabRulesKey };
}

// The state field that holds this Calculated Value, or undefined when the store
// holds none.
export const getCalculatedValueField = (
  formState: FormState,
  { sectionId, fieldId, index, property }: Omit<CalculatedValueWrite, 'value'>
) => getStateFieldFromFormState(formState, sectionId, fieldId, index, property);

interface FormStoreState {
  formState: () => FormState;
}

/**
 * Writes each Calculated Value of a tab into its form state, on init and on
 * each change, also when the tab is not open or a rule hides the field. A
 * value that only a control writes is not saved until the user opens its tab.
 *
 * `collect` returns every Calculated Value of the tab, also one whose value is
 * empty. ReportStore reads the same list through `calculatedValues` to set
 * each status. A NaN is written as an empty value, so the report saves null.
 *
 * Compose it after the `vm`, inside `withFeature`, so `collect` can read it:
 *
 * ```ts
 * withFeature((store) => withCalculatedValues(powerEvents, () => collectPowerCalculatedValues(store.vm())))
 * ```
 */
export function withCalculatedValues(
  events: Pick<FormStoreEventGroup, 'calculatedValuesChanged'>,
  collect: () => CalculatedValue[]
) {
  return signalStoreFeature(
    // No typed input. A typed input that adds props breaks the type inference
    // of withFormStoreReducer before it, so the store is cast here, as in
    // with-power-effects.ts.
    withComputed((rawStore) => {
      const store = rawStore as unknown as FormStoreState;

      const calculatedValues = computed(() =>
        collect().map((calculated) =>
          Number.isNaN(calculated.value) ? { ...calculated, value: undefined } : calculated
        )
      );

      // The paths that the store does not hold, as one text, so a change to an
      // other field does not repeat the warning. The form state is empty
      // before init and after logout, and then no path is missing.
      const missingCalculatedValuePaths = computed(() => {
        const formState = store.formState();
        if (Object.keys(formState).length === 0) return '';

        return calculatedValues()
          .filter((calculated) => !getCalculatedValueField(formState, calculated))
          .map(formatCalculatedValuePath)
          .join(', ');
      });

      const pendingCalculatedValueWrites = computed(() =>
        calculatedValues()
          .filter((calculated) => {
            const stateField = getCalculatedValueField(store.formState(), calculated);
            return stateField !== undefined && stateField.value !== calculated.value;
          })
          // The event carries the state path and the value, not the rule.
          .map(({ sectionId, fieldId, value, index, property }) => ({ sectionId, fieldId, value, index, property }))
      );

      return { calculatedValues, missingCalculatedValuePaths, pendingCalculatedValueWrites };
    }),
    withEventHandlers((store) => ({
      writeCalculatedValues$: toObservable(store.pendingCalculatedValueWrites).pipe(
        filter((writes) => writes.length > 0),
        map((writes) => events.calculatedValuesChanged({ writes }))
      ),
      // A Calculated Value with no state field is never saved. Each tab store
      // must create a field for it in every init mode.
      warnAboutMissingStateFields$: toObservable(store.missingCalculatedValuePaths).pipe(
        filter((paths) => paths !== '' && isDevMode()),
        tap((paths) => console.warn(`withCalculatedValues: the store holds no field at ${paths}`))
      ),
    }))
  );
}
